import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FiLayers, FiEdit2, FiRefreshCw, FiX, FiSave, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import useEscapeClose from '../../hooks/useEscapeClose';
import {
  MasterHeaderActions, MasterAddButton, ContentCard, StatsRow, StatCard, StatIcon, StatInfo, StatValue, StatLabel,
  Table, Th, Tr, Td, StatusBadge, SaveButton, FormGrid, FormGroup, Label, Input, Select,
} from '../../components/SharedStyles';
import { ensureFixedAssetCategories, loadFixedAssetCategories } from '../../utils/fixedAssetHelper';

const METHOD_LABELS = {
  reducing_balance: 'Reducing Balance (WDV)',
  straight_line: 'Straight Line',
  none: 'No Depreciation',
};

const AssetCategories = () => {
  const { activeCompany, currentUser } = useAuth();
  const { canEdit } = usePagePermissions('fixedAssets', 'assets');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  useEscapeClose(!!editing, () => setEditing(null));

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      setCategories(await loadFixedAssetCategories(activeCompany.id));
    } catch (e) {
      console.error('Error loading asset categories:', e);
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => { load(); }, [load]);

  const handleSeed = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    setSeeding(true);
    try {
      const { created } = await ensureFixedAssetCategories(activeCompany.id, currentUser.uid);
      await load();
      alert(created > 0 ? `${created} categor${created === 1 ? 'y' : 'ies'} added.` : 'All categories already present.');
    } catch (e) {
      console.error('Error seeding categories:', e);
      alert('Failed to seed categories. Ensure Chart of Accounts is initialized (run Repair COA).');
    } finally {
      setSeeding(false);
    }
  };

  const openEdit = (cat) => setEditing({
    ...cat,
    defaultMethod: cat.defaultMethod || 'reducing_balance',
    defaultRate: cat.defaultRate ?? 0,
    defaultUsefulLifeYears: cat.defaultUsefulLifeYears ?? 0,
  });

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'companies', activeCompany.id, 'fixedAssetCategories', editing.id), {
        defaultMethod: editing.defaultMethod,
        defaultRate: Number(editing.defaultRate) || 0,
        defaultUsefulLifeYears: Number(editing.defaultUsefulLifeYears) || 0,
        isActive: editing.isActive !== false,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid,
      });
      setEditing(null);
      await load();
    } catch (err) {
      console.error('Error saving category:', err);
      alert('Failed to save category.');
    } finally {
      setSaving(false);
    }
  };

  const missingAccounts = categories.filter(c => !c.assetAccountId || (c.key !== 'land' && !c.accumDepAccountId));

  return (
    <PageLayout pageTitle="Asset Categories" breadcrumb="Fixed Assets / Categories">
      <MasterHeaderActions>
        {canEdit && (
          <MasterAddButton onClick={handleSeed} disabled={seeding}>
            <FiRefreshCw /> {seeding ? 'Seeding...' : 'Seed / Repair Categories'}
          </MasterAddButton>
        )}
      </MasterHeaderActions>

      <StatsRow>
        <StatCard>
          <StatIcon $color={colors.primary.main}><FiLayers /></StatIcon>
          <StatInfo>
            <StatValue>{categories.length}</StatValue>
            <StatLabel>Categories</StatLabel>
          </StatInfo>
        </StatCard>
        <StatCard>
          <StatIcon $color={missingAccounts.length ? colors.warning.main : colors.success.main}>
            {missingAccounts.length ? <FiAlertTriangle /> : <FiCheckCircle />}
          </StatIcon>
          <StatInfo>
            <StatValue>{missingAccounts.length}</StatValue>
            <StatLabel>Missing Account Links</StatLabel>
          </StatInfo>
        </StatCard>
      </StatsRow>

      <ContentCard>
        {loading ? (
          <EmptyMsg>Loading categories...</EmptyMsg>
        ) : categories.length === 0 ? (
          <EmptyMsg>
            No asset categories yet. Click <strong>Seed / Repair Categories</strong> to create the
            FBR default set (Land, Building, Furniture, Office Equipment, Vehicles, Plant &amp; Machinery).
          </EmptyMsg>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Category</Th>
                <Th>Method</Th>
                <Th style={{ textAlign: 'right' }}>Rate %</Th>
                <Th style={{ textAlign: 'right' }}>Useful Life (yrs)</Th>
                <Th>Asset Account</Th>
                <Th>Accum. Dep. Account</Th>
                <Th>Status</Th>
                {canEdit && <Th>Actions</Th>}
              </tr>
            </thead>
            <tbody>
              {categories.map(cat => (
                <Tr key={cat.id}>
                  <Td><strong>{cat.categoryName}</strong></Td>
                  <Td>{METHOD_LABELS[cat.defaultMethod] || cat.defaultMethod}</Td>
                  <Td style={{ textAlign: 'right' }}>{cat.defaultMethod === 'none' ? '—' : `${cat.defaultRate || 0}%`}</Td>
                  <Td style={{ textAlign: 'right' }}>{cat.defaultUsefulLifeYears || '—'}</Td>
                  <Td>{cat.assetAccountCode ? `${cat.assetAccountCode}` : <Missing>not linked</Missing>}</Td>
                  <Td>{cat.key === 'land' ? '—' : (cat.accumDepAccountCode ? cat.accumDepAccountCode : <Missing>not linked</Missing>)}</Td>
                  <Td><StatusBadge $active={cat.isActive !== false}>{cat.isActive !== false ? 'Active' : 'Inactive'}</StatusBadge></Td>
                  {canEdit && (
                    <Td>
                      <IconBtn onClick={() => openEdit(cat)} title="Edit defaults"><FiEdit2 /></IconBtn>
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </ContentCard>

      {editing && createPortal(
        <Overlay onClick={() => setEditing(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Edit — {editing.categoryName}</h3>
              <CloseBtn onClick={() => setEditing(null)}><FiX /></CloseBtn>
            </ModalHeader>
            <form onSubmit={handleSave}>
              <FormGrid>
                <FormGroup>
                  <Label>Depreciation Method</Label>
                  <Select value={editing.defaultMethod} onChange={(e) => setEditing({ ...editing, defaultMethod: e.target.value })}>
                    <option value="reducing_balance">Reducing Balance (WDV)</option>
                    <option value="straight_line">Straight Line</option>
                    <option value="none">No Depreciation</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Annual Rate % (reducing balance)</Label>
                  <Input type="number" min="0" max="100" step="0.01" value={editing.defaultRate}
                    disabled={editing.defaultMethod !== 'reducing_balance'}
                    onChange={(e) => setEditing({ ...editing, defaultRate: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label>Useful Life in Years (straight line)</Label>
                  <Input type="number" min="0" step="1" value={editing.defaultUsefulLifeYears}
                    disabled={editing.defaultMethod !== 'straight_line'}
                    onChange={(e) => setEditing({ ...editing, defaultUsefulLifeYears: e.target.value })} />
                </FormGroup>
              </FormGrid>
              <ModalFooter>
                <SaveButton type="submit" disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save'}</SaveButton>
              </ModalFooter>
            </form>
          </ModalCard>
        </Overlay>,
        document.body
      )}
    </PageLayout>
  );
};

const EmptyMsg = styled.div`
  padding: 40px 20px; text-align: center; color: ${colors.text.secondary}; line-height: 1.6;
`;
const Missing = styled.span`color: ${colors.warning.dark}; font-style: italic; font-size: 12px;`;
const IconBtn = styled.button`
  background: none; border: 1px solid ${colors.border.light}; border-radius: 6px; padding: 6px 8px;
  cursor: pointer; color: ${colors.text.secondary}; display: inline-flex; align-items: center;
  &:hover { color: ${colors.primary.main}; border-color: ${colors.primary.main}; }
`;
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center;
  justify-content: center; z-index: 1000; padding: 16px;
`;
const ModalCard = styled.div`
  background: ${colors.background.paper}; border-radius: 12px; width: 100%; max-width: 520px;
  padding: 20px; box-shadow: 0 20px 50px rgba(0,0,0,0.3);
`;
const ModalHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px;
  h3 { margin: 0; color: ${colors.text.primary}; font-size: 18px; }
`;
const CloseBtn = styled.button`
  background: none; border: none; cursor: pointer; color: ${colors.text.secondary}; font-size: 20px; display: flex;
`;
const ModalFooter = styled.div`
  display: flex; justify-content: flex-end; margin-top: 20px; gap: 8px;
`;

export default AssetCategories;
